import { logbookNavbar } from "@dexteel/mesf-core";
import { COMPARISON_PERMISSION } from "@/pages/reports/rollshop-comparison/models";
import type { To } from "react-router-dom";
import { TIMELINE_PERMISSION } from "@/pages/production/operations-timeline/models";
import {
	PERMISSION_STRAIGHTENER_INFORMATION_RETRIEVAL,
	PERMISSION_STRAIGHTENER_TREND,
} from "@/pages/production/straightener-common/permissions";
import {
	routeDelaysManager,
	routeDelaysPareto,
} from "@/setup/routes/delay.routes";
import {
	routeForecastDataEntry,
	routeMillSetup,
	routeMillSetupSheet,
	routeNextCampaigns,
	routeOperationsTimeline,
	routePurchaseOrders,
	routeRollsCartridge,
	routeRollsReceived,
	routeStraightenerTrendDisplay,
	routeTrendDisplay,
} from "@/setup/routes/production.routes";
import {
	routeBilletsBetweenCobbles,
	routeBilletsProcessData,
	routeBilletsRFactor,
	routeBilletWorkOrders,
	routeCobblesReport,
	routeFirstTurnedRolls,
	routeForecastReport,
	routeGasConsumption,
	routeInformationRetrieval,
	routeLastTurnedRolls,
	routeRollTurnHistoryDetail,
	routeNewRolls,
	routePartialRollsets,
	routeProductsHistory,
	routeProductsWithNoRecipe,
	routeReturnedRolls,
	routeRMShiftReport,
	routeRollLayouts,
	routeRolls,
	routeRollsInUse,
	routePassChanges,
	routeRollsets,
	routeRollshop,
	routeRollshopComparison,
	routeStraightenerInformationRetrieval,
	routeStraightenerRuns,
	routeTimerSpeedReport,
	routeKpiShiftReport,
	routeProductTypesStatistics,
	routeShifts,
	routeProductChangesReport,
	routeStraightenerKpis,
	routeRFactorPerStand,
	routeSummaryByCloseDate,
	routeSummaryByDateRange,
} from "@/setup/routes/reports.routes";

/**
 * A leaf or a parent item.
 * - Leaf: has `to` (a route). Click navigates.
 * - Parent: has `items` (nested children) and NO `to`. Click/hover opens a
 *   side submenu — replicates the legacy IHBox 3-level navbar where
 *   "Rolling Mill > Rollshop Operations > {Rolls, Rollsets, Rollshop}" was
 *   nested.
 *
 * Mixed (both `to` and `items`) is allowed but discouraged — pick one.
 */
export interface MenuItemConfig {
	label: string;
	to?: To;
	permission?: string;
	items?: MenuItemConfig[];
}

export interface MenuConfig {
	id: string;
	label: string;
	to?: To;
	items: MenuItemConfig[];
	permission?: string;
}

// "Roll Layouts" is a top-level navbar tab that expands into Stand 1-17,
// each deep-linking to the page pre-selected on that stand (mirrors legacy).
const rollLayoutStandItems: MenuItemConfig[] = Array.from(
	{ length: 17 },
	(_, i) => ({
		label: `Stand ${i + 1}`,
		to: `${routeRollLayouts}/${i + 1}`,
	}),
);

/**
 * Every menu lists its pages alphabetically. Submenus go after the pages and
 * are sorted inside the same way. Numeric collation keeps "Stand 2" before
 * "Stand 10".
 */
const sortMenuItems = (items: MenuItemConfig[]): MenuItemConfig[] =>
	[...items]
		.map((item) =>
			item.items ? { ...item, items: sortMenuItems(item.items) } : item,
		)
		.sort(
			(a, b) =>
				Number(Boolean(a.items)) - Number(Boolean(b.items)) ||
				a.label.localeCompare(b.label, "en", {
					numeric: true,
					sensitivity: "base",
				}),
		);

/**
 * Static menu configuration for the navbar. Tabs render in this order.
 */
const menus: MenuConfig[] = [
	{
		id: "mill",
		label: "Mill",
		items: [
			{ label: "Forecast Data Entry", to: routeForecastDataEntry },
			{ label: "Next Campaigns", to: routeNextCampaigns },
			{
				label: "Operations Timeline",
				to: routeOperationsTimeline,
				permission: TIMELINE_PERMISSION,
			},
			{ label: "Rolls Cartridge", to: routeRollsCartridge },
			{ label: "Setup", to: routeMillSetup },
			{ label: "Setup Sheet", to: routeMillSetupSheet },
			{ label: "Trend Display", to: routeTrendDisplay },
			{
				label: "Reports",
				items: [
					{ label: "Billet Process Data", to: routeBilletsProcessData },
					{ label: "Billet Work Orders", to: routeBilletWorkOrders },
					{
						label: "Billets Between Cobbles",
						to: routeBilletsBetweenCobbles,
					},
					{ label: "Billets R-Factor", to: routeBilletsRFactor },
					{ label: "Cobbles Report", to: routeCobblesReport },
					{ label: "Forecast Report", to: routeForecastReport },
					{ label: "Information Retrieval", to: routeInformationRetrieval },
					{ label: "Pass Changes", to: routePassChanges },
					{ label: "Product Changes Report", to: routeProductChangesReport },
					{ label: "Products History", to: routeProductsHistory },
					{ label: "Products with no Recipe", to: routeProductsWithNoRecipe },
					{ label: "R-Factor per Stand", to: routeRFactorPerStand },
					{ label: "Timer Speed Report", to: routeTimerSpeedReport },
				],
			},
		],
	},
	{
		id: "straightener",
		label: "Straightener",
		items: [
			{
				label: "Information Retrieval",
				to: routeStraightenerInformationRetrieval,
				permission: PERMISSION_STRAIGHTENER_INFORMATION_RETRIEVAL,
			},
			{
				label: "Trend Display",
				to: routeStraightenerTrendDisplay,
				permission: PERMISSION_STRAIGHTENER_TREND,
			},
			{
				label: "Reports",
				items: [
					{ label: "KPIs", to: routeStraightenerKpis },
					{ label: "Runs", to: routeStraightenerRuns },
				],
			},
		],
	},
	{
		// Only reports that summarize the whole plant; area reports live in
		// their area's Reports submenu.
		id: "reports",
		label: "Reports",
		items: [
			{ label: "Gas Consumption", to: routeGasConsumption },
			{ label: "KPI Shift Report", to: routeKpiShiftReport },
			{ label: "Product Types Statistics", to: routeProductTypesStatistics },
			{ label: "RM Shift Report", to: routeRMShiftReport },
			{ label: "Shifts", to: routeShifts },
		],
	},
	{
		id: "rollshop",
		label: "Rollshop",
		items: [
			{ label: "Purchase Orders", to: routePurchaseOrders },
			{ label: "Rolls", to: routeRolls },
			{ label: "Rolls Received", to: routeRollsReceived },
			{ label: "Rollsets", to: routeRollsets },
			{ label: "Rollshop", to: routeRollshop },
			{
				// Legacy "Account" reports group, now nested under Rollshop.
				label: "Reports",
				items: [
					{ label: "First Turned Rolls", to: routeFirstTurnedRolls },
					{ label: "Last Turned Rolls", to: routeLastTurnedRolls },
					{ label: "New Rolls", to: routeNewRolls },
					{ label: "Partial Rollsets", to: routePartialRollsets },
					{ label: "Returned Rolls", to: routeReturnedRolls },
					{
						label: "Roll Turn History Detail",
						to: routeRollTurnHistoryDetail,
					},
					{ label: "Rolls in Use", to: routeRollsInUse },
					{
						label: "Rollshop vs MES",
						to: routeRollshopComparison,
						permission: COMPARISON_PERMISSION,
					},
					{ label: "Summary By Close Date", to: routeSummaryByCloseDate },
					{ label: "Summary By Date Range", to: routeSummaryByDateRange },
				],
			},
		],
	},
	{
		id: "stops",
		label: "Stops",
		items: [
			{ label: "Manager", to: routeDelaysManager },
			{ label: "Pareto", to: routeDelaysPareto },
		],
	},
	{
		id: "roll-layouts",
		label: "Roll Layouts",
		items: rollLayoutStandItems,
	},
];

export const menuConfigData: MenuConfig[] = menus.map((menu) => ({
	...menu,
	items: sortMenuItems(menu.items),
}));

/**
 * Logbook menu configuration converted from mesf-core format.
 */
export const logbookMenuConfig: MenuConfig = {
	id: logbookNavbar[0].id,
	label: "Logbook",
	items: logbookNavbar[0].items.map((item) => ({
		label: item.label,
		to: item.to,
	})),
};
